import React from 'react';
import PropTypes from 'prop-types';
import './TypingIndicator.css';

/**
 * Formats and displays the list of users who are currently typing.
 * Supports handling for single users, two users, three users, and many users.
 * * @param {Array<Object>} typingUsers - Array of user objects (must contain 'username' or 'name').
 */
const TypingIndicator = ({ typingUsers }) => {
    // 1. Check if anyone is typing
    if (!Array.isArray(typingUsers) || typingUsers.length === 0) {
        return null;
    }

    // 2. Extract and sanitize names
    // Assumes the items in typingUsers are User objects (e.g., { _id, username, name })
    const names = typingUsers.map(u => u.username || u.name).filter(Boolean);
    const count = names.length;
    let typingText = '';

    // 3. Construct the dynamic typing text
    if (count === 1) {
        typingText = `${names[0]} is typing...`;
    } else if (count === 2) {
        typingText = `${names[0]} and ${names[1]} are typing...`;
    } else if (count === 3) {
        // Example: Alice, Bob, and Carol are typing...
        typingText = `${names[0]}, ${names[1]}, and ${names[2]} are typing...`;
    } else if (count > 3) {
        // Example: Alice, Bob, and 5 others are typing...
        typingText = `${names[0]}, ${names[1]}, and ${count - 2} others are typing...`;
    } else {
        typingText = 'Someone is typing...';
    }

    return (
        <div className="typing-indicator" role="status" aria-live="polite">
            <span className="typing-text">{typingText}</span>
            <span className="dot"></span>
            <span className="dot"></span>
            <span className="dot"></span>
        </div>
    );
};

TypingIndicator.propTypes = {
    // 🛑 UPDATED Prop Type: expecting an array of users
    typingUsers: PropTypes.arrayOf(PropTypes.shape({
        _id: PropTypes.string,
        username: PropTypes.string,
        name: PropTypes.string,
    })).isRequired,
};

TypingIndicator.defaultProps = {
    typingUsers: [],
};

export default TypingIndicator;